import { Box, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Block } from "../../shared/Block";
import type { Tier } from "../../../services/referrals";

interface RulesCardProps {
  tier: Tier;
  isPremium: boolean;
}

/**
 * Колонки процента — по реальным линиям тира. Линии 1..3 показываем
 * отдельно, всё остальное (4..9) — агрегируем в одну колонку «4-9». Если
 * у тира настроена только одна линия — таблица тоже одноколоночная.
 */
function buildLineColumns(tier: Tier, t: TFunction) {
  const linesInTier = new Set(tier.line_percents.map((p) => p.line));
  const cols: Array<{ label: string; short: string; lines: number[] }> = [];
  for (const n of [1, 2, 3] as const) {
    if (linesInTier.has(n)) {
      cols.push({
        label: t("rules.line", { n }),
        short: `${n}L`,
        lines: [n],
      });
    }
  }
  const rest = [4, 5, 6, 7, 8, 9].filter((n) => linesInTier.has(n));
  if (rest.length > 0) {
    cols.push({ label: t("rules.lines_4_9"), short: "4-9L", lines: rest });
  }
  if (cols.length === 0) {
    cols.push({ label: t("rules.line", { n: 1 }), short: "1L", lines: [1] });
  }
  return cols;
}

function pickValue(
  tier: Tier,
  lines: number[],
  field: "deposit" | "cardIssue" | "transfer",
): string {
  const values: number[] = [];
  for (const ln of lines) {
    const p = tier.line_percents.find((x) => x.line === ln);
    if (p) values.push(p[field]);
  }
  if (values.length === 0) return "—";
  const avg = values.reduce((s, x) => s + x, 0) / values.length;
  if (avg === 0) return "0%";
  return `${avg}%`;
}

export function RulesCard({ tier, isPremium }: RulesCardProps) {
  const { t } = useTranslation("referral");
  const cols = buildLineColumns(tier, t);

  const rows = [
    {
      label: t("rules.deposits"),
      description: t("rules.deposits_desc"),
      field: "deposit" as const,
      icon: "solar:wallet-money-bold-duotone",
    },
    {
      label: t("rules.card_issue"),
      description: t("rules.card_issue_desc"),
      field: "cardIssue" as const,
      icon: "solar:card-bold-duotone",
    },
    {
      label: t("rules.transfers"),
      description: t("rules.transfers_desc"),
      field: "transfer" as const,
      icon: "solar:transfer-horizontal-bold-duotone",
    },
  ];

  return (
    <Block sx={{ marginTop: "10px", padding: "14px 16px" }}>
      {/* Заголовок карточки */}
      <Box>
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {t("rules.title", { name: tier.name })}
        </Typography>
        <Typography
          sx={{ fontSize: "11px", opacity: 0.55, marginTop: "1px" }}
        >
          {tier.is_premium
            ? t("rules.premium_activation")
            : tier.min_referrals === 0
              ? t("rules.assigned_immediately")
              : t(
                  isPremium
                    ? "rules.needed_referrals_obsolete"
                    : "rules.needed_referrals",
                  { count: tier.min_referrals },
                )}
        </Typography>
      </Box>

      {/* Строки с источниками */}
      <Box
        sx={{
          marginTop: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {rows.map((r) => (
          <Box
            key={r.field}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 12px",
              borderRadius: "14px",
              backgroundColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.02)",
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                color: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.75)"
                    : "rgba(0,0,0,0.55)",
              }}
            >
              <Icon icon={r.icon} width={18} height={18} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 600 }}>
                {r.label}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.55 }}>
                {r.description}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: "14px",
                flexShrink: 0,
                flexWrap: "nowrap",
                alignItems: "center",
              }}
            >
              {cols.map((c) => {
                const v = pickValue(tier, c.lines, r.field);
                const isZero = v === "0%" || v === "—";
                return (
                  <Box
                    key={c.short}
                    sx={{
                      minWidth: 34,
                      textAlign: "center",
                    }}
                  >
                    {cols.length > 1 && (
                      <Typography
                        sx={{
                          fontSize: "10px",
                          lineHeight: 1,
                          opacity: 0.4,
                          letterSpacing: "0.4px",
                        }}
                      >
                        {c.label.toUpperCase()}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        lineHeight: 1.2,
                        marginTop: cols.length > 1 ? "3px" : 0,
                        color: isZero ? "text.disabled" : "text.primary",
                      }}
                    >
                      {v}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Block>
  );
}
